import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Plus } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dataAPI } from '../api/dataApi';

import { resolveApiBaseUrl } from '../api/apiBase';

const API_BASE_URL = resolveApiBaseUrl();

type GroupRecord = {
    uid: string;
    title: string;
    description: string;
};

type UserRecord = {
    uid: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    groups?: string[];
};

function getUserDisplayName(user: UserRecord): string {
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    if (name) return name;
    return user.email || user.uid;
}

async function patchUserGroups(uid: string, groups: string[]): Promise<void> {
    const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (!token) {
        throw new Error('Authentication token not found');
    }

    const response = await fetch(`${API_BASE_URL}/auth/admin/user/${encodeURIComponent(uid)}/groups`, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ groups }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update user groups' }));
        throw new Error(errorData.message || 'Failed to update user groups');
    }
}

async function listGroups(): Promise<GroupRecord[]> {
    const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (!token) throw new Error('Authentication token not found');

    const response = await fetch(`${API_BASE_URL}/auth/admin/groups`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to load groups' }));
        throw new Error(errorData.message || 'Failed to load groups');
    }
    const data = await response.json();
    const groups = Array.isArray(data?.groups) ? data.groups : [];
    return groups
        .map((g: any) => ({
            uid: g?.uid || g?.id || g?._id || '',
            title: typeof g?.title === 'string' ? g.title : '',
            description: typeof g?.description === 'string' ? g.description : '',
        }))
        .filter((g: GroupRecord) => g.uid && g.title);
}

async function createGroup(payload: { title: string; description: string }): Promise<void> {
    const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (!token) throw new Error('Authentication token not found');
    const response = await fetch(`${API_BASE_URL}/auth/admin/groups`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to create group' }));
        throw new Error(errorData.message || 'Failed to create group');
    }
}

async function updateGroup(uid: string, payload: { title: string; description: string }): Promise<void> {
    const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (!token) throw new Error('Authentication token not found');
    const response = await fetch(`${API_BASE_URL}/auth/admin/groups/${encodeURIComponent(uid)}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to update group' }));
        throw new Error(errorData.message || 'Failed to update group');
    }
}

async function removeGroup(uid: string): Promise<void> {
    const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
    if (!token) throw new Error('Authentication token not found');
    const response = await fetch(`${API_BASE_URL}/auth/admin/groups/${encodeURIComponent(uid)}`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to delete group' }));
        throw new Error(errorData.message || 'Failed to delete group');
    }
}

const AdminGroupsPage: React.FC = () => {
    const [groups, setGroups] = useState<GroupRecord[]>([]);
    const [users, setUsers] = useState<UserRecord[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [newTitle, setNewTitle] = useState('');
    const [newDescription, setNewDescription] = useState('');

    const [editingUid, setEditingUid] = useState<string | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');

    const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
    const [activeGroupForMemberAdd, setActiveGroupForMemberAdd] = useState<GroupRecord | null>(null);
    const [selectedUserUidForMemberAdd, setSelectedUserUidForMemberAdd] = useState<string>('');
    const [addGroupDialogOpen, setAddGroupDialogOpen] = useState(false);
    const [activeGroupTab, setActiveGroupTab] = useState<string>('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [groupsResponse, usersResponse] = await Promise.all([
                listGroups(),
                dataAPI.getUsersList(),
            ]);
            const mappedGroups: GroupRecord[] = groupsResponse;

            const rawUsers = Array.isArray(usersResponse?.peers) ? usersResponse.peers : [];
            const mappedUsers: UserRecord[] = rawUsers
                .map((u: any) => ({
                    uid: u?.uid || u?.id || u?._id || '',
                    email: u?.email,
                    firstName: u?.firstName || u?.info?.firstName,
                    lastName: u?.lastName || u?.info?.lastName,
                    groups: Array.isArray(u?.groups)
                        ? u.groups.filter((groupId: unknown) => typeof groupId === 'string')
                        : Array.isArray(u?.groupIds)
                            ? u.groupIds.filter((groupId: unknown) => typeof groupId === 'string')
                            : [],
                }))
                .filter((u: UserRecord) => u.uid);

            mappedGroups.sort((a, b) => a.title.localeCompare(b.title));
            mappedUsers.sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)));

            setGroups(mappedGroups);
            setUsers(mappedUsers);
        } catch (err: any) {
            console.error('Error loading groups page data:', err);
            setError(err.message || 'Failed to load groups');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData().catch(() => undefined);
    }, [loadData]);

    useEffect(() => {
        if (groups.length === 0) {
            setActiveGroupTab('');
            return;
        }
        if (activeGroupTab && !groups.some((group) => group.uid === activeGroupTab)) {
            setActiveGroupTab('');
        }
    }, [groups, activeGroupTab]);

    const membersByGroup = useMemo(() => {
        const map = new Map<string, UserRecord[]>();
        for (const group of groups) {
            map.set(group.uid, []);
        }
        for (const user of users) {
            const userGroups = Array.isArray(user.groups) ? user.groups : [];
            for (const groupUid of userGroups) {
                const arr = map.get(groupUid);
                if (arr) arr.push(user);
            }
        }
        return map;
    }, [groups, users]);

    const usersWithoutGroup = useMemo(() => {
        const activeGroupUid = activeGroupForMemberAdd?.uid;
        if (!activeGroupUid) return users;
        return users.filter((u) => !(u.groups || []).includes(activeGroupUid));
    }, [users, activeGroupForMemberAdd]);

    const handleCreateGroup = async (): Promise<boolean> => {
        const title = newTitle.trim();
        if (!title) {
            setError('Group title is required');
            return false;
        }
        setSaving(true);
        setError(null);
        try {
            await createGroup({
                title,
                description: newDescription.trim(),
            });
            setNewTitle('');
            setNewDescription('');
            await loadData();
            return true;
        } catch (err: any) {
            console.error('Error creating group:', err);
            setError(err.message || 'Failed to create group');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const beginEditGroup = (group: GroupRecord) => {
        setEditingUid(group.uid);
        setEditTitle(group.title);
        setEditDescription(group.description || '');
        setError(null);
    };

    const cancelEdit = () => {
        setEditingUid(null);
        setEditTitle('');
        setEditDescription('');
    };

    const handleSaveEditGroup = async (group: GroupRecord) => {
        const title = editTitle.trim();
        if (!title) {
            setError('Group title is required');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await updateGroup(group.uid, {
                title,
                description: editDescription.trim(),
            });
            cancelEdit();
            await loadData();
        } catch (err: any) {
            console.error('Error updating group:', err);
            setError(err.message || 'Failed to update group');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteGroup = async (group: GroupRecord) => {
        const members = membersByGroup.get(group.uid) || [];
        if (members.length > 0) {
            setError(`Cannot delete "${group.title}" because it still has members. Remove members first.`);
            return;
        }
        if (!window.confirm(`Delete group "${group.title}"?`)) {
            return;
        }

        setSaving(true);
        setError(null);
        try {
            await removeGroup(group.uid);
            await loadData();
        } catch (err: any) {
            console.error('Error deleting group:', err);
            setError(err.message || 'Failed to delete group');
        } finally {
            setSaving(false);
        }
    };

    const handleAddMember = async () => {
        const activeGroupUid = typeof activeGroupForMemberAdd?.uid === 'string'
            ? activeGroupForMemberAdd.uid.trim()
            : '';
        if (!activeGroupUid || !selectedUserUidForMemberAdd) {
            setError('Select a user to add');
            return;
        }
        const targetUser = users.find((u) => u.uid === selectedUserUidForMemberAdd);
        if (!targetUser) {
            setError('Selected user not found');
            return;
        }

        const existing = Array.isArray(targetUser.groups)
            ? targetUser.groups.map((groupId) => String(groupId).trim()).filter(Boolean)
            : [];
        if (existing.includes(activeGroupUid)) {
            setError('User is already in this group');
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const nextGroups = Array.from(new Set([...existing, activeGroupUid]));
            await patchUserGroups(targetUser.uid, nextGroups);
            setSelectedUserUidForMemberAdd('');
            setAddMemberDialogOpen(false);
            setActiveGroupForMemberAdd(null);
            await loadData();
        } catch (err: any) {
            console.error('Error adding member to group:', err);
            setError(err.message || 'Failed to add member');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveMember = async (groupUid: string, user: UserRecord) => {
        const existing = Array.isArray(user.groups) ? user.groups : [];
        const nextGroups = existing.filter((g) => g !== groupUid);

        setSaving(true);
        setError(null);
        try {
            await patchUserGroups(user.uid, nextGroups);
            await loadData();
        } catch (err: any) {
            console.error('Error removing member from group:', err);
            setError(err.message || 'Failed to remove member');
        } finally {
            setSaving(false);
        }
    };

    const totalMemberships = useMemo(
        () => users.reduce((count, user) => count + (Array.isArray(user.groups) ? user.groups.length : 0), 0),
        [users]
    );

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Groups</h1>
                    <p className="text-gray-600 dark:text-gray-400">
                    Manage group title and description, and add or remove members.
                    </p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        onClick={() => setAddGroupDialogOpen(true)}
                        disabled={saving || loading}
                    >
                        Add group
                    </Button>
                    <Badge variant="secondary">{groups.length} groups</Badge>
                    <Badge variant="secondary">{users.length} users</Badge>
                    <Badge variant="secondary">{totalMemberships} memberships</Badge>
                </div>
            </div>

            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <div className="space-y-4">
                {loading && <p className="text-sm text-gray-500">Loading groups...</p>}
                {!loading && groups.length === 0 && (
                    <p className="text-sm text-gray-500">No groups found.</p>
                )}

                {groups.length > 0 && (
                    <Tabs value={activeGroupTab} onValueChange={setActiveGroupTab} className="w-full">
                        <TabsList className="w-full justify-start overflow-x-auto bg-slate-100 dark:bg-slate-900">
                            {groups.map((group) => (
                                <TabsTrigger
                                    key={group.uid}
                                    value={group.uid}
                                    className="data-[state=active]:bg-blue-600 data-[state=active]:text-white data-[state=active]:shadow-sm"
                                >
                                    {group.title}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                        {!activeGroupTab && (
                            <Card className="mt-3">
                                <CardContent className="pt-6 text-sm text-gray-600 dark:text-gray-300">
                                    Select a group tab to view and manage details.
                                </CardContent>
                            </Card>
                        )}
                        {groups.map((group) => {
                            const members = membersByGroup.get(group.uid) || [];
                            const isEditing = editingUid === group.uid;
                            return (
                                <TabsContent key={group.uid} value={group.uid}>
                                    <Card className="h-full">
                                        <CardHeader className="pb-2">
                                            <CardTitle className="flex items-center justify-between gap-3">
                                                <span className="truncate">{group.title}</span>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <Button
                                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                                        onClick={() => {
                                                            setActiveGroupForMemberAdd(group);
                                                            setSelectedUserUidForMemberAdd('');
                                                            setAddMemberDialogOpen(true);
                                                        }}
                                                        disabled={saving}
                                                        aria-label={`Add member to ${group.title}`}
                                                        title="Add member"
                                                    >
                                                        <Plus className="h-4 w-4 mr-1" />
                                                        Add member
                                                    </Button>
                                                    <Badge variant="secondary">{members.length} members</Badge>
                                                </div>
                                            </CardTitle>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {isEditing ? (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Title</Label>
                                                        <Input
                                                            value={editTitle}
                                                            onChange={(e) => setEditTitle(e.target.value)}
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Description</Label>
                                                        <Input
                                                            value={editDescription}
                                                            onChange={(e) => setEditDescription(e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-sm text-gray-600 dark:text-gray-400 min-h-5">
                                                    {group.description || 'No description'}
                                                </p>
                                            )}

                                            <div className="flex flex-wrap gap-2">
                                                {isEditing ? (
                                                    <>
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleSaveEditGroup(group)}
                                                            disabled={saving}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={cancelEdit}
                                                            disabled={saving}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => beginEditGroup(group)}
                                                        disabled={saving}
                                                    >
                                                        Edit
                                                    </Button>
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="destructive"
                                                    onClick={() => handleDeleteGroup(group)}
                                                    disabled={saving}
                                                >
                                                    Remove
                                                </Button>
                                            </div>

                                            <div className="space-y-2">
                                                <Label>Members</Label>
                                                {members.length === 0 ? (
                                                    <p className="text-sm text-gray-500">No members in this group.</p>
                                                ) : (
                                                    <div className="space-y-2 max-h-64 overflow-auto pr-1">
                                                        {members.map((member) => (
                                                            <div
                                                                key={`${group.uid}:${member.uid}`}
                                                                className="flex items-center justify-between rounded border p-2"
                                                            >
                                                                <div className="text-sm min-w-0">
                                                                    <div className="font-medium truncate">{getUserDisplayName(member)}</div>
                                                                    <div className="text-xs text-muted-foreground truncate">{member.email || member.uid}</div>
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleRemoveMember(group.uid, member)}
                                                                    disabled={saving}
                                                                >
                                                                    Remove
                                                                </Button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                </TabsContent>
                            );
                        })}
                    </Tabs>
                )}
            </div>

            <Dialog
                open={addGroupDialogOpen}
                onOpenChange={(open) => {
                    setAddGroupDialogOpen(open);
                    if (!open) {
                        setNewTitle('');
                        setNewDescription('');
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add group</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="group-title">Title</Label>
                            <Input
                                id="group-title"
                                value={newTitle}
                                onChange={(e) => setNewTitle(e.target.value)}
                                placeholder="e.g. Editors"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="group-description">Description</Label>
                            <Input
                                id="group-description"
                                value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)}
                                placeholder="Optional description"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setAddGroupDialogOpen(false);
                                setNewTitle('');
                                setNewDescription('');
                            }}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={async () => {
                                const ok = await handleCreateGroup();
                                if (ok) {
                                    setAddGroupDialogOpen(false);
                                }
                            }}
                            disabled={saving || loading}
                        >
                            {saving ? 'Saving...' : 'Add group'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog
                open={addMemberDialogOpen}
                onOpenChange={(open) => {
                    setAddMemberDialogOpen(open);
                    if (!open) {
                        setActiveGroupForMemberAdd(null);
                        setSelectedUserUidForMemberAdd('');
                    }
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Add member {activeGroupForMemberAdd ? `to ${activeGroupForMemberAdd.title}` : ''}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label>User</Label>
                        <Select
                            value={selectedUserUidForMemberAdd}
                            onValueChange={setSelectedUserUidForMemberAdd}
                            disabled={!activeGroupForMemberAdd}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select user" />
                            </SelectTrigger>
                            <SelectContent>
                                {usersWithoutGroup.map((u) => (
                                    <SelectItem key={u.uid} value={u.uid}>
                                        {getUserDisplayName(u)} ({u.email || u.uid})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setAddMemberDialogOpen(false);
                                setActiveGroupForMemberAdd(null);
                                setSelectedUserUidForMemberAdd('');
                            }}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddMember}
                            disabled={saving || loading || !activeGroupForMemberAdd || !selectedUserUidForMemberAdd}
                        >
                            {saving ? 'Saving...' : 'Add member'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminGroupsPage;
